import { useEffect, useMemo, useState } from 'react';

const stringSortCollator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
});

export type SortDirection = 'asc' | 'desc' | null;

export type SortConfig<T> = {
  key: keyof T;
  direction: SortDirection;
} | null;

export type UseTableOptions<T> = {
  data: T[];
  initialSort?: { key: keyof T; direction: 'asc' | 'desc' };
  sortConfig?: SortConfig<T>;
  onSortChange?: (sortConfig: SortConfig<T>) => void;
  pageSize?: number;
  searchQuery?: string;
  searchKeys?: (keyof T)[];
  /** Custom sort value accessor. When provided, overrides default key-based sorting for the given key. */
  getSortValue?: (key: keyof T, row: T) => string | number | null | undefined;
};

export type UseTableReturn<T> = {
  // Data
  currentData: T[];
  sortedData: T[];
  filteredData: T[];

  // Sorting
  sortConfig: SortConfig<T>;
  handleSort: (key: keyof T) => void;

  // Pagination
  currentPage: number;
  totalPages: number;
  setCurrentPage: (page: number) => void;
  nextPage: () => void;
  prevPage: () => void;
  canNextPage: boolean;
  canPrevPage: boolean;

  // Selection
  selectedRows: Set<number>;
  toggleRow: (index: number) => void;
  toggleAll: () => void;
  isRowSelected: (index: number) => boolean;
  isAllSelected: boolean;
  clearSelection: () => void;
};

/**
 * useTable Hook - Client-side table state management
 * Handles filtering, sorting, pagination, and selection
 */
export function useTable<T>({
  data,
  initialSort,
  sortConfig: controlledSortConfig,
  onSortChange,
  pageSize = 10,
  searchQuery = '',
  searchKeys,
  getSortValue,
}: UseTableOptions<T>): UseTableReturn<T> {
  const [internalSortConfig, setInternalSortConfig] = useState<SortConfig<T>>(
    initialSort
      ? { key: initialSort.key, direction: initialSort.direction }
      : null
  );
  const sortConfig = controlledSortConfig ?? internalSortConfig;
  const isSortControlled = controlledSortConfig !== undefined;
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());

  // Reset to page 1 when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Filtering logic
  const filteredData = useMemo(() => {
    if (!searchQuery || searchQuery.trim() === '') return data;

    const query = searchQuery.toLowerCase();

    return data.filter((row) => {
      // If searchKeys are specified, only search those fields
      if (searchKeys && searchKeys.length > 0) {
        return searchKeys.some((key) => {
          const value = row[key];
          if (value == null) return false;
          return String(value).toLowerCase().includes(query);
        });
      }

      // Otherwise, search all fields
      return Object.values(row as Record<string, unknown>).some((value) => {
        if (value == null) return false;
        return String(value).toLowerCase().includes(query);
      });
    });
  }, [data, searchQuery, searchKeys]);

  // Sorting logic
  const sortedData = useMemo(() => {
    if (!sortConfig) return filteredData;

    const sorted = [...filteredData].sort((a, b) => {
      const aValue = getSortValue
        ? getSortValue(sortConfig.key, a)
        : (a[sortConfig.key] as string | number | null | undefined);
      const bValue = getSortValue
        ? getSortValue(sortConfig.key, b)
        : (b[sortConfig.key] as string | number | null | undefined);

      if (aValue === bValue) return 0;

      // Handle null/undefined
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = stringSortCollator.compare(aValue, bValue);
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      }

      // Compare values
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      return sortConfig.direction === 'asc' ? 1 : -1;
    });

    return sorted;
  }, [filteredData, sortConfig, getSortValue]);

  // Pagination logic
  const totalPages = Math.ceil(sortedData.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentData = sortedData.slice(startIndex, endIndex);

  // Sorting handler
  const handleSort = (key: keyof T) => {
    let direction: SortDirection = 'asc';

    if (sortConfig?.key === key) {
      if (sortConfig.direction === 'asc') {
        direction = 'desc';
      } else if (sortConfig.direction === 'desc') {
        direction = null;
      }
    }

    const nextSortConfig = direction ? { key, direction } : null;

    if (!isSortControlled) {
      setInternalSortConfig(nextSortConfig);
    }

    onSortChange?.(nextSortConfig);
    setCurrentPage(1); // Reset to first page on sort
  };

  // Pagination handlers
  const nextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const prevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const canNextPage = currentPage < totalPages;
  const canPrevPage = currentPage > 1;

  // Selection handlers
  const toggleRow = (index: number) => {
    setSelectedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const toggleAll = () => {
    if (selectedRows.size === currentData.length) {
      setSelectedRows(new Set());
    } else {
      const allIndices = currentData.map((_, idx) => startIndex + idx);
      setSelectedRows(new Set(allIndices));
    }
  };

  const isRowSelected = (index: number) => selectedRows.has(index);

  const isAllSelected =
    currentData.length > 0 &&
    currentData.every((_, idx) => selectedRows.has(startIndex + idx));

  const clearSelection = () => setSelectedRows(new Set());

  return {
    currentData,
    sortedData,
    filteredData,
    sortConfig,
    handleSort,
    currentPage,
    totalPages,
    setCurrentPage,
    nextPage,
    prevPage,
    canNextPage,
    canPrevPage,
    selectedRows,
    toggleRow,
    toggleAll,
    isRowSelected,
    isAllSelected,
    clearSelection,
  };
}

export default useTable;
